import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    getWhatsAppSettings,
    saveWhatsAppSettings,
    getRecentWhatsAppQueue,
    testWhatsAppSettings,
} from '../../api/whatsapp';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { useRegionalFormDefaults } from '../../context/RegionalSettingsContext';
import { formatDateTime } from '../../utils/regionalFormat';
import { TextInput, CheckboxField } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage, safeUserMessage } from '../../utils/apiError';

const TEMPLATE_KEYS = [
    'templateSessionReminder',
    'templateAbsenceAlert',
    'templateLowBalance',
    'templateFeeReminder',
    'templatePendingSessions',
    'templateLowBalanceAdmin',
    'templateSessionCancelled',
    'templateHolidayAnnouncement',
    'templateCorrespondenceDeadline',
    'templateCorrespondenceYourMove',
];

function StatusChip({ on, t }) {
    return (
        <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
            }`}
        >
            {on ? t('settings.report.statusOn') : t('settings.report.statusOff')}
        </span>
    );
}

export default function WhatsAppSettings() {
    const { t } = useTranslation();
    const regional = useRegionalFormDefaults();
    const [settings, setSettings] = useState(null);
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newToken, setNewToken] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [testPhone, setTestPhone] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [s, q] = await Promise.all([
                getWhatsAppSettings(),
                getRecentWhatsAppQueue(),
            ]);
            setSettings(s);
            setQueue(Array.isArray(q) ? q : []);
        } catch {
            setError(t('settings.whatsapp.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

    const dirtyValue = settings ? { settings, newToken } : null;
    const persist = async () => {
        const payload = {
            ...settings,
            apiToken: newToken.trim() ? newToken.trim() : settings.apiToken,
        };
        const saved = await saveWhatsAppSettings(payload);
        setSettings(saved);
        setNewToken('');
        const q = await getRecentWhatsAppQueue();
        setQueue(Array.isArray(q) ? q : []);
        return { settings: saved, newToken: '' };
    };
    const { markSaved } = useUnsavedChanges(dirtyValue, {
        enabled: !!settings,
        onSave: async () => { await persist(); },
    });

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const next = await persist();
            markSaved(next);
            setSuccess(t('settings.whatsapp.saveSuccess'));
        } catch (err) {
            setError(apiErrorMessage(err, t('settings.whatsapp.saveFailed')));
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        setError('');
        try {
            const result = await testWhatsAppSettings(testPhone.trim());
            setTestResult(result);
            if (!result?.success) {
                setError(safeUserMessage(result?.message, t('settings.whatsapp.testFailed')));
            }
        } catch (err) {
            setError(apiErrorMessage(err, t('settings.whatsapp.testFailed')));
        } finally {
            setTesting(false);
        }
    };

    const statusLabel = useMemo(() => {
        if (!settings) return '';
        if (!settings.licenseWhatsAppEnabled) return t('settings.whatsapp.modeUnlicensed');
        if (settings.enabled) return t('settings.whatsapp.modeEnabled');
        return t('settings.whatsapp.modeOff');
    }, [settings, t]);

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t('settings.whatsapp.title')}
                subtitle={t('settings.whatsapp.loadingSubtitle')}
            />
        );
    }

    if (!settings) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t('nav.system')}
                    title={t('settings.whatsapp.title')}
                    subtitle={t('settings.whatsapp.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status">
                    {error || t('settings.whatsapp.loadFailed')}
                </div>
            </div>
        );
    }

    const licensed = settings.licenseWhatsAppEnabled;
    const tokenConfigured = settings.apiToken === 'CONFIGURED';
    const hasChannel = !!(settings.channelNumber || '').trim();
    const canEnable = licensed && (settings.mockMode || ((tokenConfigured || newToken.trim()) && hasChannel));
    const canSave = !settings.enabled || canEnable;

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.whatsapp.title')}
                subtitle={t('settings.whatsapp.subtitle')}
            />

            <form onSubmit={save} className="space-y-6">
                {!licensed && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <Trans i18nKey="settings.whatsapp.notLicensed" components={{ strong: <strong /> }} />
                    </div>
                )}
                {success && (
                    <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{success}</div>
                )}
                {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
                )}

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t('settings.whatsapp.stepOne')}</p>
                                <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.whatsapp.connectionSection')}</h2>
                                <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('settings.whatsapp.connectionSectionDesc')}</p>
                            </div>
                            <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                                <p className="text-xs font-medium text-slate-500">{t('settings.whatsapp.statusLabel')}</p>
                                <p className="text-sm font-semibold text-slate-900">{statusLabel}</p>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${settings.enabled ? 'border-primary-200 bg-primary-50/40' : 'border-slate-200 bg-slate-50/60'}`}>
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-900">{t('settings.whatsapp.enableTitle')}</p>
                                    <StatusChip on={!!settings.enabled} t={t} />
                                </div>
                                <p className="mt-1 text-sm text-slate-600">{t('settings.whatsapp.enableHint')}</p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={settings.enabled}
                                disabled={!licensed}
                                onClick={() => update('enabled', !settings.enabled)}
                                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${settings.enabled ? 'bg-primary-600' : 'bg-slate-200'} ${!licensed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${settings.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                        </div>

                        <CheckboxField
                            layout="toggle-card"
                            label={t('settings.whatsapp.mockMode')}
                            description={t('settings.whatsapp.mockModeDesc')}
                            checked={!!settings.mockMode}
                            disabled={!licensed}
                            onChange={(e) => update('mockMode', e.target.checked)}
                        />

                        <div className="grid gap-4 md:grid-cols-2">
                            <TextInput
                                label={t('settings.whatsapp.apiBaseUrl')}
                                className="font-mono text-sm"
                                placeholder="https://live-server.wati.io"
                                value={settings.apiBaseUrl || ''}
                                disabled={!licensed}
                                onChange={(e) => update('apiBaseUrl', e.target.value)}
                                note={t('settings.whatsapp.apiBaseUrlNote')}
                            />
                            <TextInput
                                label={t('settings.whatsapp.channelNumber')}
                                placeholder={regional.phoneFormatHint || `${regional.phoneDialCode} ${regional.phoneLocalPlaceholder}`}
                                value={settings.channelNumber || ''}
                                disabled={!licensed}
                                onChange={(e) => update('channelNumber', e.target.value)}
                                note={t('settings.whatsapp.channelNumberNote')}
                            />
                            <TextInput
                                label={t('settings.whatsapp.bearerToken')}
                                type="password"
                                className="font-mono text-sm md:col-span-2"
                                fieldClassName="md:col-span-2"
                                placeholder={tokenConfigured ? t('settings.email.passwordConfigured') : t('settings.whatsapp.tokenPlaceholder')}
                                value={newToken}
                                disabled={!licensed}
                                onChange={(e) => setNewToken(e.target.value)}
                                autoComplete="new-password"
                            />
                        </div>

                        {settings.enabled && !canSave && (
                            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                {t('settings.whatsapp.enableWarning')}
                            </p>
                        )}

                        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <p className="text-sm font-semibold text-slate-900">{t('settings.whatsapp.testTitle')}</p>
                            <p className="text-sm text-slate-600">{t('settings.whatsapp.testDesc')}</p>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                                <TextInput
                                    label={t('settings.whatsapp.testPhone')}
                                    className="font-mono text-sm"
                                    placeholder={regional.phoneFormatHint || `${regional.phoneDialCode} …`}
                                    value={testPhone}
                                    disabled={!licensed}
                                    onChange={(e) => setTestPhone(e.target.value)}
                                    fieldClassName="flex-1"
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={!licensed || testing}
                                    onClick={runTest}
                                >
                                    {testing ? t('settings.whatsapp.testing') : t('settings.whatsapp.sendTest')}
                                </Button>
                            </div>
                            {testResult?.success && (
                                <p className="text-sm text-emerald-700">
                                    {testResult.message}
                                    {testResult.templateName
                                        ? ` (${testResult.templateName})`
                                        : ''}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t('settings.whatsapp.stepTwo')}</p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.whatsapp.templatesSection')}</h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('settings.whatsapp.templatesSectionDesc')}</p>
                        <p className="mt-2 max-w-2xl text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                            {t('settings.whatsapp.localeTemplateNote', {
                                lang: (regional.localeTag || 'en').split('-')[0].toLowerCase(),
                                example: `${settings.templateSessionReminder || 'chessdesk_session_reminder'}_${(regional.localeTag || 'en').split('-')[0].toLowerCase()}`,
                            })}
                        </p>
                    </div>
                    <div className="grid gap-3 px-5 py-5 md:grid-cols-2 sm:px-6">
                        {TEMPLATE_KEYS.map((key) => (
                            <TextInput
                                key={key}
                                label={t(`settings.whatsapp.${key}`)}
                                className="font-mono text-xs"
                                value={settings[key] || ''}
                                disabled={!licensed}
                                onChange={(e) => update(key, e.target.value)}
                            />
                        ))}
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t('settings.whatsapp.stepThree')}</p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.whatsapp.recentLog')}</h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('settings.whatsapp.recentLogDesc')}</p>
                    </div>
                    {queue.length === 0 ? (
                        <p className="px-5 py-8 text-sm text-slate-500 sm:px-6">{t('settings.whatsapp.noMessages')}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.whatsapp.when')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.whatsapp.phone')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.whatsapp.type')}</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide">{t('settings.whatsapp.status')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {queue.slice(0, 15).map((row) => (
                                        <tr key={row.id} className="text-slate-700">
                                            <td className="whitespace-nowrap px-4 py-2.5 text-xs">{row.createdAt ? formatDateTime(row.createdAt) : '—'}</td>
                                            <td className="px-4 py-2.5 font-mono text-xs">{row.recipientPhone}</td>
                                            <td className="px-4 py-2.5">{t(`settings.whatsapp.messageTypes.${row.messageType}`, { defaultValue: String(row.messageType || '').replace(/_/g, ' ') })}</td>
                                            <td className="px-4 py-2.5">
                                                <span className={row.status === 'SENT' ? 'text-emerald-700' : row.status === 'FAILED' ? 'text-red-600' : 'text-slate-600'}>
                                                    {t(`settings.whatsapp.queueStatus.${row.status}`, {
                                                        defaultValue: String(row.status || '').replace(/_/g, ' '),
                                                    })}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <div className="sticky bottom-4 z-10 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving || !licensed || !canSave}
                        className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        {saving ? t('forms.common.saving') : t('settings.whatsapp.saveSettings')}
                    </button>
                </div>
            </form>
        </div>
    );
}
